// src/components/address/AddressForm.jsx
import React, { useState, useEffect } from 'react';
import Input from '../common/Input';
import Button from '../common/Button';
import { isValidPhone, isValidPincode } from '../../utils/helpers';

/**
 * Address Form Component
 * 
 * Used in:
 * - CheckoutPage (add new address)
 * - AddressesPage (add/edit address)
 * 
 * Features:
 * - Add new address
 * - Edit existing address
 * - Form validation
 * - Set as default option
 */
const AddressForm = ({ 
  address = null, 
  onSubmit, 
  onCancel, 
  loading = false 
}) => {
  const [formData, setFormData] = useState({
    fullName: '',
    phone: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    pincode: '',
    country: 'India',
    isDefault: false,
  });
  
  const [errors, setErrors] = useState({});
  const [pincodeLoading, setPincodeLoading] = useState(false);

  // Load address data for editing
  useEffect(() => {
    if (address) {
      setFormData(address);
    }
  }, [address]);
  
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }

    if (name === 'pincode' && value.length === 6 && /^\d{6}$/.test(value)) {
      setPincodeLoading(true);
      fetch(`https://api.postalpincode.in/pincode/${value}`)
        .then(r => r.json())
        .then(([data]) => {
          if (data.Status === 'Success') {
            const po = data.PostOffice[0];
            setFormData(prev => ({ ...prev, city: po.District, state: po.State }));
            setErrors(prev => ({ ...prev, city: '', state: '', pincode: '' }));
          }
        })
        .catch(() => {})
        .finally(() => setPincodeLoading(false));
    }
  };
  
  const validate = () => {
    const newErrors = {};
    
    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Full name is required';
    }
    
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!isValidPhone(formData.phone)) {
      newErrors.phone = 'Invalid phone number (10 digits required)';
    }
    
    if (!formData.addressLine1.trim()) {
      newErrors.addressLine1 = 'Address is required';
    }
    
    if (!formData.city.trim()) {
      newErrors.city = 'City is required';
    }
    
    if (!formData.state.trim()) {
      newErrors.state = 'State is required';
    }
    
    if (!formData.pincode.trim()) {
      newErrors.pincode = 'Pincode is required';
    } else if (!isValidPincode(formData.pincode)) {
      newErrors.pincode = 'Invalid pincode (6 digits required)';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validate()) {
      onSubmit(formData);
    }
  };
  
  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Full Name */}
      <Input
        label="Full Name"
        name="fullName"
        value={formData.fullName}
        onChange={handleChange}
        placeholder="Enter full name"
        required
        error={errors.fullName}
      />
      
      {/* Phone */}
      <Input
        label="Phone Number"
        name="phone"
        type="tel"
        value={formData.phone}
        onChange={handleChange}
        placeholder="10 digit phone number"
        required
        maxLength={10}
        error={errors.phone}
      />
      
      {/* Address Line 1 */}
      <Input
        label="Address Line 1"
        name="addressLine1"
        value={formData.addressLine1}
        onChange={handleChange}
        placeholder="House no., Building name"
        required
        error={errors.addressLine1}
      />
      
      {/* Address Line 2 */}
      <Input
        label="Address Line 2 (Optional)"
        name="addressLine2"
        value={formData.addressLine2}
        onChange={handleChange}
        placeholder="Road name, Area, Colony"
      />
      
      {/* City & State */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          label="City"
          name="city"
          value={formData.city}
          onChange={handleChange}
          placeholder="City"
          required
          error={errors.city}
        />
        
        <Input
          label="State"
          name="state"
          value={formData.state}
          onChange={handleChange}
          placeholder="State"
          required
          error={errors.state}
        />
      </div>
      
      {/* Pincode & Country */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          label={pincodeLoading ? 'Pincode (fetching...)' : 'Pincode'}
          name="pincode"
          value={formData.pincode}
          onChange={handleChange}
          placeholder="6 digit pincode"
          required
          maxLength={6}
          error={errors.pincode}
        />
        
        <Input
          label="Country"
          name="country"
          value={formData.country}
          onChange={handleChange}
          disabled
        />
      </div>
      
      {/* Set as Default */}
      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          id="isDefault"
          name="isDefault"
          checked={formData.isDefault}
          onChange={handleChange}
          className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
        />
        <label htmlFor="isDefault" className="text-sm text-gray-700">
          Set as default address
        </label>
      </div>
      
      {/* Buttons */}
      <div className="flex gap-3 pt-4">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          fullWidth
        >
          Cancel
        </Button>
        <Button
          type="submit"
          loading={loading}
          fullWidth
        >
          {address ? 'Update Address' : 'Add Address'}
        </Button>
      </div>
    </form>
  );
};

export default AddressForm;